import React from 'react';
import styled from 'styled-components';
import { SearchBarWithDropdown, Input } from '@tw/tw-component-library';
import {
  useClientMatterSearch,
  FormattedClientData,
  FormattedMatterData,
} from '../../../../hooks/data/clientMatter/useClientMatterSearch';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.secondary || '#666'};
  margin-bottom: 1rem;
`;

const ClientMatterContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const WorkspaceNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export interface WorkspaceDetailsStepProps {
  workspaceData: {
    type?: 'client-matter' | 'non-client-matter';
    name: string;
    description: string;
    matterId?: string;
    clientId?: string;
    client?: FormattedClientData;
    matter?: FormattedMatterData;
  };
  onUpdate: (
    updates: Partial<{
      name: string;
      description: string;
      matterId?: string;
      clientId?: string;
      client?: FormattedClientData;
      matter?: FormattedMatterData;
    }>
  ) => void;
}

export const WorkspaceDetailsStep: React.FC<WorkspaceDetailsStepProps> = ({
  workspaceData,
  onUpdate,
}) => {
  const {
    clientData,
    matterData,
    formattedClientData,
    formattedMatterData,
    selectedClientUno,
    selectedMatterUno,
    handleClientSearch,
    handleMatterSearch,
    handleClientSelect,
    handleMatterSelect,
    isLoadingClients,
    isLoadingMatters,
    showMatterDropdown,
    showClientDropdown,
    setShowClientDropdown,
    setShowMatterDropdown,
  } = useClientMatterSearch('UX');

  const handleClientSearchWrapper = (searchValue: string) => {
    // When input is cleared, clear selection and workspace data
    if (!searchValue) {
      handleClientSelect('');
      onUpdate({ clientId: undefined, client: undefined });
    }

    handleClientSearch(searchValue);
  };

  const handleMatterSearchWrapper = (searchValue: string) => {
    // When input is cleared, clear selection and workspace data
    if (!searchValue) {
      handleMatterSelect('');
      onUpdate({ matterId: undefined, matter: undefined });
    }

    handleMatterSearch(searchValue);
  };

  const handleClientSelectWrapper = (option: string) => {
    handleClientSelect(option);
    // Handle clearing the selection
    if (!option) {
      onUpdate({ clientId: undefined, client: undefined });
      return;
    }

    const selectedClient = formattedClientData.find(
      (client) => client.label === option
    );
    if (!selectedClient) {
      onUpdate({ clientId: undefined, client: undefined });
      return;
    }
    // Store UNO for API and full client for later use
    onUpdate({ clientId: selectedClient.value, client: selectedClient });
  };

  const handleMatterSelectWrapper = (option: string) => {
    handleMatterSelect(option);
    // Handle clearing the selection
    if (!option) {
      onUpdate({ matterId: undefined, matter: undefined });
      return;
    }

    const selectedMatter = formattedMatterData.find(
      (matter) => matter.label === option
    );
    if (!selectedMatter) {
      onUpdate({ matterId: undefined, matter: undefined });
      return;
    }
    // Store UNO for API and full matter for later use
    onUpdate({ matterId: selectedMatter.value, matter: selectedMatter });
  };

  return (
    <Container>
      <>
        <Title>Workspace Details</Title>
        <Description>Provide information about your workspace</Description>
      </>

      {workspaceData.type === 'client-matter' && (
        <ClientMatterContainer>
          <SearchBarWithDropdown
            label="Client"
            options={clientData}
            placeholder="Client..."
            onSelect={(option) => handleClientSelectWrapper(option || '')}
            onSearch={(searchValue) => handleClientSearchWrapper(searchValue)}
            searchButton={false}
            value={selectedClientUno}
            isOpenOverride={showClientDropdown}
            setIsOpenOverride={setShowClientDropdown}
            isLoading={isLoadingClients}
            getDisplayText={(option: string) => option || ''}
            required
          />
          <SearchBarWithDropdown
            label="Matter"
            options={matterData}
            placeholder="Matter..."
            onSelect={(option) => handleMatterSelectWrapper(option || '')}
            onSearch={handleMatterSearchWrapper}
            searchButton={false}
            value={selectedMatterUno}
            isOpenOverride={showMatterDropdown}
            setIsOpenOverride={setShowMatterDropdown}
            isLoading={isLoadingMatters}
            getDisplayText={(option: string) => option || ''}
            required
          />
        </ClientMatterContainer>
      )}

      <WorkspaceNameContainer data-tour="create-ws-details-name-desc">
        <Input
          type="text"
          value={workspaceData.name}
          label="Workspace Name"
          required
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="Enter workspace name"
        />

        <Input
          type="textArea"
          value={workspaceData.description}
          label="Description"
          required
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Enter workspace description"
        />
      </WorkspaceNameContainer>
    </Container>
  );
};
